"use client";

import {
  Check,
  Mail,
  MessageCircle,
  MessagesSquare,
  Phone,
  Sparkles,
  Video,
  type LucideIcon,
} from "lucide-react";
import { SiFacebook, SiWhatsapp } from "react-icons/si";
import { Button } from "~/components/ui/button";
import { DemoCTA, PrimaryCTA } from "~/components/siteglue/siteglue-cta";
import { marketingAppDashboardUrl } from "~/lib/auth-paths";
import { MARKETING_BUILD_CUSTOM_NEW } from "~/lib/inbound-gpt/paths";
import { getMarketingChrome } from "~/lib/siteglue/marketing-chrome";
import { cn } from "~/lib/utils";

const HOME_HERO_CHANNELS: {
  label: string;
  Icon: LucideIcon | typeof SiWhatsapp | typeof SiFacebook;
}[] = [
  { label: "Phone Calls", Icon: Phone },
  { label: "Emails", Icon: Mail },
  { label: "Live Chat", Icon: MessageCircle },
  { label: "Text Messages", Icon: MessagesSquare },
  { label: "Video Calls", Icon: Video },
  { label: "WhatsApp Messages", Icon: SiWhatsapp },
  { label: "Facebook Messages", Icon: SiFacebook },
];

const HOME_HERO_TRUST = [
  "No code required",
  "No credit card required",
  "HIPAA compliant",
] as const;

type HomeHeroTrishOnboardingProps = {
  variant?: "home" | "standalone";
  className?: string;
};

/** Marketing homepage hero — primary CTA opens hire flow; SiteGlue secondary opens live demo gallery. */
export function HomeHeroTrishOnboarding({
  variant = "standalone",
  className,
}: HomeHeroTrishOnboardingProps) {
  if (variant !== "standalone") return null;

  const chrome = getMarketingChrome();
  const hireCustomHref = marketingAppDashboardUrl(MARKETING_BUILD_CUSTOM_NEW);
  const isSiteGlue = chrome.brandId === "siteglue";

  return (
    <div className={cn("w-full", className)}>
      <div className="mx-auto flex max-w-4xl flex-col items-center text-center">
        <div className="mb-5 flex items-center gap-2">
          <Sparkles
            className="h-4 w-4"
            style={{ color: isSiteGlue ? chrome.accentColor : undefined }}
            aria-hidden
          />
          <span
            className={cn(
              "text-sm font-medium",
              !isSiteGlue && "text-slate-700",
            )}
            style={isSiteGlue ? { color: chrome.primaryColor } : undefined}
          >
            {chrome.heroEyebrow}
          </span>
          <span
            className="rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white"
            style={{
              backgroundColor: isSiteGlue ? chrome.accentColor : "#f97316",
            }}
          >
            Beta
          </span>
        </div>

        <h1 className="max-w-3xl text-balance text-[clamp(1.75rem,3vw+0.75rem,3.25rem)] font-bold leading-[1.15] tracking-tight text-slate-900">
          {chrome.heroHeadline}
        </h1>

        <p className="mt-4 max-w-2xl text-balance text-base text-slate-600 sm:text-lg">
          {chrome.heroSubheading}
        </p>

        <ul className="mt-7 flex max-w-4xl flex-wrap items-start justify-center gap-x-5 gap-y-4 px-2 sm:gap-x-7">
          {HOME_HERO_CHANNELS.map(({ label, Icon }) => (
            <li
              key={label}
              className="flex w-[4.75rem] flex-col items-center gap-1.5 text-center sm:w-[5.5rem]"
            >
              <span
                className="flex h-10 w-10 items-center justify-center"
                style={{ color: isSiteGlue ? chrome.primaryColor : undefined }}
              >
                <Icon
                  className={cn("h-5 w-5", !isSiteGlue && "text-slate-600")}
                  aria-hidden
                />
              </span>
              <span className="text-[11px] font-medium leading-tight text-slate-600 sm:text-xs">
                {label}
              </span>
            </li>
          ))}
        </ul>

        <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row">
          {isSiteGlue ? (
            <>
              <PrimaryCTA location="hero" testId="button-home-hero-signup" />
              <DemoCTA location="hero" testId="button-home-hero-live-demo" />
            </>
          ) : (
            <>
              <Button
                asChild
                size="lg"
                className="rounded-full bg-slate-900 px-8 text-base text-white hover:bg-slate-800"
              >
                <a href={hireCustomHref} target="_blank" rel="noopener noreferrer">
                  Try It Free
                </a>
              </Button>
              <Button
                type="button"
                size="lg"
                variant="outline"
                className="rounded-full border-slate-300 bg-white px-8 text-base text-slate-900 hover:bg-slate-50"
                asChild
              >
                <a href="/features#demo">Watch Video</a>
              </Button>
            </>
          )}
        </div>

        <ul className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm text-slate-500">
          {HOME_HERO_TRUST.map((item) => (
            <li key={item} className="flex items-center gap-1.5">
              <Check
                className="h-3.5 w-3.5"
                style={{ color: isSiteGlue ? chrome.accentColor : undefined }}
                aria-hidden
              />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
